import React, { useState } from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Calendar, Clock, MapPin, User, Wrench, AlertCircle, Package, ChevronLeft, ChevronRight, Key } from 'lucide-react';
interface SchedulingProps {
  onLogout: () => void;
}
interface ScheduledEvent {
  id: string;
  type: 'onsite' | 'installation' | 'license' | 'deadline';
  title: string;
  client: string;
  date: string;
  time: string;
  technician?: string;
  ticketNumber?: string;
  priority: 'high' | 'medium' | 'low';
  status: 'scheduled' | 'in_progress' | 'completed';
}
const mockEvents: ScheduledEvent[] = [{
  id: '1',
  type: 'onsite',
  title: 'Vor-Ort Support - Netzwerkproblem',
  client: 'Müller GmbH',
  date: '2024-01-15',
  time: '09:00',
  technician: 'Max Mustermann',
  ticketNumber: '#1042',
  priority: 'high',
  status: 'scheduled'
}, {
  id: '2',
  type: 'license',
  title: 'Microsoft 365 Lizenzen laufen aus',
  client: 'Schmidt KG',
  date: '2024-01-18',
  time: '00:00',
  priority: 'high',
  status: 'scheduled'
}, {
  id: '3',
  type: 'installation',
  title: 'Installation neuer Firewall',
  client: 'Praxis Dr. Stein',
  date: '2024-01-16',
  time: '14:00',
  technician: 'Lisa Schmidt',
  ticketNumber: '#1045',
  priority: 'medium',
  status: 'scheduled'
}, {
  id: '4',
  type: 'deadline',
  title: 'Backup-System Wartung fällig',
  client: 'Müller GmbH',
  date: '2024-01-17',
  time: '10:00',
  priority: 'medium',
  status: 'scheduled'
}, {
  id: '5',
  type: 'onsite',
  title: 'Drucker-Installation',
  client: 'StartUp Rocket',
  date: '2024-01-19',
  time: '11:00',
  technician: 'Max Mustermann',
  ticketNumber: '#1048',
  priority: 'low',
  status: 'scheduled'
}, {
  id: '6',
  type: 'license',
  title: 'Adobe Creative Cloud Renewal',
  client: 'Schmidt KG',
  date: '2024-01-20',
  time: '00:00',
  priority: 'medium',
  status: 'scheduled'
}, {
  id: '7',
  type: 'installation',
  title: 'Server-Migration',
  client: 'Müller GmbH',
  date: '2024-01-22',
  time: '08:00',
  technician: 'Lisa Schmidt',
  ticketNumber: '#1050',
  priority: 'high',
  status: 'scheduled'
}];
export function Scheduling({
  onLogout
}: SchedulingProps) {
  const [currentDate] = useState(new Date(2024, 0, 15)); // January 15, 2024
  const [view, setView] = useState<'week' | 'list'>('week');
  const getEventIcon = (type: ScheduledEvent['type']) => {
    switch (type) {
      case 'onsite':
        return <MapPin className="h-4 w-4" data-id="element-1107" />;
      case 'installation':
        return <Wrench className="h-4 w-4" data-id="element-1108" />;
      case 'license':
        return <Key className="h-4 w-4" data-id="element-1109" />;
      case 'deadline':
        return <AlertCircle className="h-4 w-4" data-id="element-1110" />;
      default:
        return <Calendar className="h-4 w-4" data-id="element-1111" />;
    }
  };
  const getEventColor = (type: ScheduledEvent['type']) => {
    switch (type) {
      case 'onsite':
        return 'bg-blue-100 border-blue-300 text-blue-700';
      case 'installation':
        return 'bg-purple-100 border-purple-300 text-purple-700';
      case 'license':
        return 'bg-amber-100 border-amber-300 text-amber-700';
      case 'deadline':
        return 'bg-red-100 border-red-300 text-red-700';
      default:
        return 'bg-slate-100 border-slate-300 text-slate-700';
    }
  };
  const getPriorityBadge = (priority: ScheduledEvent['priority']) => {
    switch (priority) {
      case 'high':
        return <Badge variant="error" data-id="element-1112">Hoch</Badge>;
      case 'medium':
        return <Badge variant="warning" data-id="element-1113">Mittel</Badge>;
      case 'low':
        return <Badge variant="secondary" data-id="element-1114">Niedrig</Badge>;
    }
  };
  const getWeekDays = () => {
    const days = [];
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay() + 1); // Start on Monday
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };
  const getEventsForDay = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return mockEvents.filter(event => event.date === dateStr);
  };
  const weekDays = getWeekDays();
  return <MainLayout title="Einsatzplanung" onLogout={onLogout} data-id="element-1115">
      {/* Header Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6" data-id="element-1116">
        <div className="flex items-center gap-4" data-id="element-1117">
          <div className="flex items-center gap-2" data-id="element-1118">
            <Button variant="ghost" size="icon" data-id="element-1119">
              <ChevronLeft className="h-5 w-5" data-id="element-1120" />
            </Button>
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-1121">
              KW 3 - Januar 2024
            </h2>
            <Button variant="ghost" size="icon" data-id="element-1122">
              <ChevronRight className="h-5 w-5" data-id="element-1123" />
            </Button>
          </div>
          <Button variant="outline" size="sm" data-id="element-1124">
            Heute
          </Button>
        </div>

        <div className="flex items-center gap-2" data-id="element-1125">
          <Button variant={view === 'week' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('week')} data-id="element-1126">
            Wochenansicht
          </Button>
          <Button variant={view === 'list' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('list')} data-id="element-1127">
            Listenansicht
          </Button>
          <Button size="sm" leftIcon={<Calendar className="h-4 w-4" data-id="element-1129" />} data-id="element-1128">
            Neuer Einsatz
          </Button>
        </div>
      </div>

      {/* Legend */}
      <Card className="mb-6" data-id="element-1130">
        <div className="flex flex-wrap items-center gap-4" data-id="element-1131">
          <h3 className="text-sm font-semibold text-slate-700" data-id="element-1132">Legende:</h3>
          <div className="flex items-center gap-2" data-id="element-1133">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 border border-blue-300 rounded text-xs text-blue-700" data-id="element-1134">
              <MapPin className="h-3 w-3" data-id="element-1135" />
              Vor-Ort Einsatz
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-100 border border-purple-300 rounded text-xs text-purple-700" data-id="element-1136">
              <Wrench className="h-3 w-3" data-id="element-1137" />
              Installation
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-100 border border-amber-300 rounded text-xs text-amber-700" data-id="element-1138">
              <Key className="h-3 w-3" data-id="element-1139" />
              Lizenz
            </div>
            <div className="flex items-center gap-1.5 px-2 py-1 bg-red-100 border border-red-300 rounded text-xs text-red-700" data-id="element-1140">
              <AlertCircle className="h-3 w-3" data-id="element-1141" />
              Frist
            </div>
          </div>
        </div>
      </Card>

      {view === 'week' /* Week View */ ? <Card className="overflow-hidden" data-id="element-1142">
          <div className="grid grid-cols-7 border-b border-slate-200" data-id="element-1143">
            {weekDays.map((day, index) => {
          const isToday = day.toDateString() === new Date(2024, 0, 15).toDateString();
          return <div key={index} className={`p-4 text-center border-r border-slate-200 last:border-r-0 ${isToday ? 'bg-teal-50' : ''}`} data-id="element-1144">
                  <div className="text-xs font-medium text-slate-500 uppercase" data-id="element-1145">
                    {day.toLocaleDateString('de-DE', {
                weekday: 'short'
              })}
                  </div>
                  <div className={`text-2xl font-bold mt-1 ${isToday ? 'text-teal-600' : 'text-slate-900'}`} data-id="element-1146">
                    {day.getDate()}
                  </div>
                </div>;
        })}
          </div>

          <div className="grid grid-cols-7" data-id="element-1147">
            {weekDays.map((day, dayIndex) => {
          const events = getEventsForDay(day);
          const isToday = day.toDateString() === new Date(2024, 0, 15).toDateString();
          return <div key={dayIndex} className={`min-h-[400px] p-2 border-r border-slate-200 last:border-r-0 ${isToday ? 'bg-teal-50/30' : ''}`} data-id="element-1148">
                  <div className="space-y-2" data-id="element-1149">
                    {events.map(event => <div key={event.id} className={`p-2 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${getEventColor(event.type)}`} data-id="element-1150">
                        <div className="flex items-start gap-1.5 mb-1" data-id="element-1151">
                          {getEventIcon(event.type)}
                          <div className="flex-1 min-w-0" data-id="element-1152">
                            <p className="text-xs font-semibold truncate" data-id="element-1153">
                              {event.title}
                            </p>
                            <p className="text-xs opacity-75 truncate" data-id="element-1154">
                              {event.client}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs opacity-75" data-id="element-1155">
                          <Clock className="h-3 w-3" data-id="element-1156" />
                          {event.time}
                        </div>
                        {event.technician && <div className="flex items-center gap-1 text-xs opacity-75 mt-1" data-id="element-1157">
                            <User className="h-3 w-3" data-id="element-1158" />
                            {event.technician.split(' ')[0]}
                          </div>}
                      </div>)}
                  </div>
                </div>;
        })}
          </div>
        </Card> /* List View */ : <Card data-id="element-1159">
          <div className="space-y-3" data-id="element-1160">
            {mockEvents.sort((a, b) => new Date(a.date + ' ' + a.time).getTime() - new Date(b.date + ' ' + b.time).getTime()).map(event => <div key={event.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-all cursor-pointer" data-id="element-1161">
                  <div className={`p-3 rounded-lg ${getEventColor(event.type)}`} data-id="element-1162">
                    {getEventIcon(event.type)}
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-1163">
                    <div className="flex items-center gap-2 mb-1" data-id="element-1164">
                      <h3 className="font-semibold text-slate-900" data-id="element-1165">
                        {event.title}
                      </h3>
                      {getPriorityBadge(event.priority)}
                      {event.ticketNumber && <Badge variant="outline" data-id="element-1166">{event.ticketNumber}</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600" data-id="element-1167">
                      <span className="flex items-center gap-1" data-id="element-1168">
                        <MapPin className="h-3 w-3" data-id="element-1169" />
                        {event.client}
                      </span>
                      {event.technician && <span className="flex items-center gap-1" data-id="element-1170">
                          <User className="h-3 w-3" data-id="element-1171" />
                          {event.technician}
                        </span>}
                    </div>
                  </div>

                  <div className="text-right" data-id="element-1172">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-1173">
                      {new Date(event.date).toLocaleDateString('de-DE', {
                day: '2-digit',
                month: 'short'
              })}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 justify-end mt-1" data-id="element-1174">
                      <Clock className="h-3 w-3" data-id="element-1175" />
                      {event.time}
                    </div>
                  </div>

                  <Button variant="outline" size="sm" data-id="element-1176">
                    Details
                  </Button>
                </div>)}
          </div>
        </Card>}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6" data-id="element-1177">
        <Card data-id="element-1178">
          <div className="flex items-center gap-3" data-id="element-1179">
            <div className="p-3 bg-blue-100 rounded-lg" data-id="element-1180">
              <MapPin className="h-5 w-5 text-blue-600" data-id="element-1181" />
            </div>
            <div data-id="element-1182">
              <p className="text-sm text-slate-600" data-id="element-1183">Vor-Ort Einsätze</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-1184">2</p>
            </div>
          </div>
        </Card>

        <Card data-id="element-1185">
          <div className="flex items-center gap-3" data-id="element-1186">
            <div className="p-3 bg-purple-100 rounded-lg" data-id="element-1187">
              <Wrench className="h-5 w-5 text-purple-600" data-id="element-1188" />
            </div>
            <div data-id="element-1189">
              <p className="text-sm text-slate-600" data-id="element-1190">Installationen</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-1191">2</p>
            </div>
          </div>
        </Card>

        <Card data-id="element-1192">
          <div className="flex items-center gap-3" data-id="element-1193">
            <div className="p-3 bg-amber-100 rounded-lg" data-id="element-1194">
              <Key className="h-5 w-5 text-amber-600" data-id="element-1195" />
            </div>
            <div data-id="element-1196">
              <p className="text-sm text-slate-600" data-id="element-1197">Lizenzen</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-1198">2</p>
            </div>
          </div>
        </Card>

        <Card data-id="element-1199">
          <div className="flex items-center gap-3" data-id="element-1200">
            <div className="p-3 bg-red-100 rounded-lg" data-id="element-1201">
              <AlertCircle className="h-5 w-5 text-red-600" data-id="element-1202" />
            </div>
            <div data-id="element-1203">
              <p className="text-sm text-slate-600" data-id="element-1204">Fristen</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-1205">1</p>
            </div>
          </div>
        </Card>
      </div>
    </MainLayout>;
}